import React, { useState, useContext, useEffect } from "react";
import { API_URL } from "src/scenes/App";
import axios from "axios";
import CancellationModal from "../components/CancellationModal";
import PreSubscriptionActionPrompt from "../components/PreSubscriptionActionPrompt";
import {
    LottieLoadingIndicator,
    PortalMetricCard,
    PortalMetricGrid,
    PortalMetricLabel,
    PortalMetricValue,
    SettingsActionButton,
    SettingsBodyText,
    SettingsDangerActionButton,
    SettingsInfoBox,
    SettingsPanelCaption,
    SettingsPanelHeader,
    SettingsPanelStack,
    SettingsPanelTitle,
    SettingsSectionBody,
    SettingsSectionCard,
    SettingsSectionHeader,
    SettingsSectionTitle,
    Toast
} from "src/components";

import { SettingContext } from "../SettingContext";
import { SubscriptionBadge, SubscriptionStatus } from "src/components/SubscriptionElements";
import MoreMenuContainer from "../components/MoreMenuContainerSubscription";
import { UserContext } from "src/scenes/App/UserContext";
import { UserKeys } from "src/constants/userDetails";
import { formatCurrency } from "src/utils/currency";
import { withRouter, Link } from "react-router-dom";
import styled from "styled-components";

import VisaLogo from "../../../img/airwallex/Visa.svg";
import MastercardLogo from "../../../img/airwallex/Mastercard.svg";
import AmexLogo from "../../../img/airwallex/Amex.svg";
import GenericLogo from "../../../img/airwallex/GenericCard.svg";

const HeaderMiddle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 0 0 auto;
  margin: 0 12px;
`;

const SwitchLabel = styled.label`
  position: relative;
  display: inline-block;
  width: 46px;
  height: 26px;
`;

const SwitchInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;

  &:disabled + span {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SwitchSlider = styled.span`
  position: absolute;
  inset: 0;
  cursor: pointer;
  background: #c9d2e3;
  border-radius: 999px;
  transition: background 0.2s ease;

  &::before {
    content: "";
    position: absolute;
    height: 20px;
    width: 20px;
    left: 3px;
    top: 3px;
    background: #fff;
    border-radius: 50%;
    transition: transform 0.2s ease;
    box-shadow: 0 1px 2px rgba(0,0,0,0.25);
  }

  /* ON state */
  ${SwitchInput}:checked + & { background: #36be24; }
  ${SwitchInput}:checked + &::before { transform: translateX(20px); }
`;

function Subscription({ ...props }) {
    const {
        getCompanyDetails,
        subscriptionStart,
        subscriptionEnd,
        subscriptionStatus,
        subscriptionNextPaymentDate,
        inDemoMode,
        totalNumber,
        employees,
        clients,
        token,
        subscriptionPeriod,
        subscriptionDurationUnit,
        subscriptionPaymentPeriod,
        emailPro
    } = useContext(SettingContext);
    const userContext = useContext(UserContext);
    const activeUsers = (employees + clients);
    const [loading, setLoading] = useState(false);
    const [showCancellationModal, setShowCancellationModal] = useState(false);
    const [showReactivationModal, setShowReactivationModal] = useState(false);
    const [showRenewalModal, setShowRenewalModal] = useState(false);

    // Payment method
    const [pmLoading, setPmLoading] = useState(true);
    const [paymentMethod, setPaymentMethod] = useState(null);
    const emailProLabel = emailPro?.title
        ? emailPro.title.split(" ").pop()
        : emailPro?.purchasedQuantity ?? "-";

    const companyDetails = {
        companyName: userContext[UserKeys.COMPANY_NAME],
        subscriptionEnd: subscriptionEnd
    };

    // Next Payment Amount
    const [currentSubscription, setCurrentSubscription] = useState(null);
    const subscriptionTitle = currentSubscription ? `${currentSubscription.name} Subscription` : "";

    const cancelSubscription = () => {
        setShowCancellationModal(false);
        setLoading(true);

        axios.put(`${API_URL}/company/cancel`, {}, {
            headers: {
                Authorization: "Bearer " + token
            }
        }).then (async _ => {
            await getCompanyDetails();
            setLoading(false);

            Toast.info("Your subscription has been cancelled.");
        }).catch(error => {
            Toast.error(error.getApiErrorDetails().message);
        });
    };

    const reactivationSubscription = () => {
        setShowReactivationModal(false);
        setLoading(true);
        axios.put(`${API_URL}/company/reactivate`, {}, {
            headers: {
                Authorization: "Bearer " + token
            }
        }).then (async _ => {
            await getCompanyDetails();
            setLoading(false);

            Toast.info("Your subscription has been reactivated.");
        }).catch(error => {
            Toast.error(error.getApiErrorDetails().message);
        });
    };

    const renewalSubscription = () => {
        props.history.push("/renewal");
    };

    const handleCloseCancellationModal = () => setShowCancellationModal(false);
    const handleCloseReactivationModal = () => setShowReactivationModal(false);
    const handleCloseRenewalModal = () => setShowRenewalModal(false);

    const getPaymentMethods = () => {
        setPmLoading(true);
        axios.get(`${API_URL}/company/airwallex/paymentMethods`, {
            headers: {
                Authorization: "Bearer " + token
            }
        }).then(res => {
            const list = Array.isArray(res.data?.paymentMethods) ? res.data.paymentMethods : [];
            const chosen = list.find(p => p.isDefault) || list[0] || null;
            setPaymentMethod(chosen);
            setPmLoading(false);
        }).catch(error => {
            setPmLoading(false);
            Toast.error(error.getApiErrorDetails().message);
        });
    };

    useEffect(() => {
        getPaymentMethods();
    }, []);

    // Card Brand Logos
    const brandLogos = {
        visa: VisaLogo,
        mastercard: MastercardLogo,
        amex: AmexLogo,
    };

    // Next Payment Amount
    useEffect(() => {
        async function fetchPlan() {
            try {
                const response = await axios.get(`${API_URL}/company`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                setCurrentSubscription(response.data.currentSubscription ?? null);
            } catch (err) {
                console.error("Error fetching current subscription", err);
            }
        }
        fetchPlan();
    }, [token, subscriptionDurationUnit, subscriptionPeriod, subscriptionPaymentPeriod]);

    // Subscription status booleans
    const isAutoRenewOn = subscriptionStatus !== SubscriptionStatus.REACTIVATE;
    const isReactivationRequired = !inDemoMode && subscriptionStatus === SubscriptionStatus.REACTIVATE;
    const isPaymentRequired = !inDemoMode && (
        subscriptionStatus === SubscriptionStatus.EXPIRED ||
        subscriptionStatus === SubscriptionStatus.OVERDUE
    );
    const nextPaymentAmount = currentSubscription
        ? (currentSubscription.nextPaymentInclGstCents / 100) + (emailPro?.price ?? 0)
        : null;

    const onToggleAutoRenew = (next) => {
        if (next) {
            setShowReactivationModal(true);
        } else {
            setShowCancellationModal(true);
        }
    };

    if (loading) {
        return <LottieLoadingIndicator />;
    }

    return (
        <>
            <SettingsPanelStack>
                <SettingsPanelHeader withDivider>
                    <div>
                        <SettingsPanelTitle>Subscription Overview</SettingsPanelTitle>
                        <SettingsPanelCaption>
                            Easily view your current subscription tier, payment history, license count, user activity, and storage usage. Stay informed with upcoming payment details and manage your billing method at any time.
                        </SettingsPanelCaption>
                    </div>
                </SettingsPanelHeader>

                <SettingsSectionCard overflow="visible">
                    <SettingsSectionHeader
                        justifyContent="space-between"
                        background="#171A22"
                        borderColor="#171A22"
                        borderRadius="15px 15px 0 0"
                    >
                        <SettingsSectionTitle color="#FFFFFF">{subscriptionTitle || "Subscription"}</SettingsSectionTitle>
                        <HeaderMiddle>
                            {!inDemoMode && (
                                <>
                                    <SettingsBodyText size="0.8125rem" color="#FFFFFF">Auto renew subscription</SettingsBodyText>
                                    <SwitchLabel aria-label="Toggle auto-renew">
                                        <SwitchInput
                                            type="checkbox"
                                            checked={isAutoRenewOn}
                                            onChange={(event) => onToggleAutoRenew(event.target.checked)}
                                        />
                                        <SwitchSlider />
                                    </SwitchLabel>
                                </>
                            )}
                            <SubscriptionBadge status={subscriptionStatus} inDemoMode={inDemoMode}>
                                {inDemoMode ? "DEMO" : subscriptionStatus}
                            </SubscriptionBadge>
                            {!inDemoMode && (
                                <MoreMenuContainer
                                    status={subscriptionStatus}
                                    cancelSubscriptionEvent={setShowCancellationModal}
                                    reactivationSubscriptionEvent={setShowReactivationModal}
                                    renewalSubscriptionEvent={setShowRenewalModal}
                                />
                            )}
                        </HeaderMiddle>
                    </SettingsSectionHeader>

                    <SettingsSectionBody>
                        <PortalMetricGrid columns="4">
                            <PortalMetricCard>
                                <PortalMetricLabel>Subscription start date</PortalMetricLabel>
                                <PortalMetricValue size="1.25rem">{inDemoMode ? "-" : subscriptionStart}</PortalMetricValue>
                            </PortalMetricCard>
                            <PortalMetricCard
                                background={isPaymentRequired ? "#FFF5F5" : undefined}
                                border={isPaymentRequired ? "1px solid #F3B5B5" : undefined}
                            >
                                <PortalMetricLabel color={isPaymentRequired ? "#D64545" : undefined}>Subscription end date</PortalMetricLabel>
                                <PortalMetricValue size="1.25rem" color={isPaymentRequired ? "#D64545" : undefined}>
                                    {inDemoMode ? "-" : subscriptionEnd}
                                </PortalMetricValue>
                            </PortalMetricCard>
                            <PortalMetricCard>
                                <PortalMetricLabel>Total licenses</PortalMetricLabel>
                                <PortalMetricValue size="1.25rem">{totalNumber}</PortalMetricValue>
                            </PortalMetricCard>
                            <PortalMetricCard>
                                <PortalMetricLabel>Active users</PortalMetricLabel>
                                <PortalMetricValue size="1.25rem">{activeUsers}</PortalMetricValue>
                            </PortalMetricCard>
                            <PortalMetricCard>
                                <PortalMetricLabel>Last payment</PortalMetricLabel>
                                <PortalMetricValue size="1.25rem">{inDemoMode ? "-" : subscriptionStart}</PortalMetricValue>
                            </PortalMetricCard>
                            <PortalMetricCard>
                                <PortalMetricLabel>Upcoming payment</PortalMetricLabel>
                                <PortalMetricValue size="1.25rem">{inDemoMode ? "-" : subscriptionNextPaymentDate}</PortalMetricValue>
                            </PortalMetricCard>
                            <PortalMetricCard>
                                <PortalMetricLabel>Email Pro</PortalMetricLabel>
                                <PortalMetricValue size="1.25rem">{inDemoMode ? "-" : emailProLabel}</PortalMetricValue>
                            </PortalMetricCard>
                        </PortalMetricGrid>

                        {isReactivationRequired && (
                            <Reactivate setShowReactivationModal={setShowReactivationModal} />
                        )}
                        {isPaymentRequired && <Expired setShowRenewalModal={setShowRenewalModal} />}

                        {!inDemoMode && (
                            <SettingsInfoBox>
                                <div>
                                    <SettingsSectionTitle>Payment method</SettingsSectionTitle>
                                    {pmLoading && <SettingsBodyText>Loading...</SettingsBodyText>}
                                    {!pmLoading && !paymentMethod && (
                                        <SettingsBodyText>No payment method on file.</SettingsBodyText>
                                    )}
                                    {!pmLoading && paymentMethod && (
                                        <>
                                            <SettingsBodyText>
                                                <img
                                                    src={brandLogos[paymentMethod.card?.brand] || GenericLogo}
                                                    alt={paymentMethod.card?.brand || "card"}
                                                    height="30"
                                                />
                                                {" "}•••• {paymentMethod.card?.last4}
                                            </SettingsBodyText>
                                            <SettingsBodyText>
                                                Next payment amount: {nextPaymentAmount
                                                    ? formatCurrency(nextPaymentAmount, currentSubscription.currencyCode, true)
                                                    : "-"}
                                            </SettingsBodyText>
                                        </>
                                    )}
                                </div>
                                <SettingsActionButton as={Link} to="/setting/payment" minWidth="14rem">
                                    Update payment method
                                </SettingsActionButton>
                            </SettingsInfoBox>
                        )}
                    </SettingsSectionBody>
                </SettingsSectionCard>
            </SettingsPanelStack>

            {showCancellationModal &&
                <CancellationModal
                    showDialog={showCancellationModal}
                    handleClose={() => handleCloseCancellationModal()}
                    companyDetails={companyDetails}
                    confirmCallback={() => cancelSubscription()}
                />
            }

            {showReactivationModal &&
                <PreSubscriptionActionPrompt
                    actionName="Reactivate"
                    showDialog={showReactivationModal}
                    handleClose={() => handleCloseReactivationModal()}
                    companyDetails={companyDetails}
                    confirmCallback={() => reactivationSubscription()}
                />
            }

            {showRenewalModal &&
                <PreSubscriptionActionPrompt
                    actionName="Renew"
                    showDialog={showRenewalModal}
                    handleClose={() => handleCloseRenewalModal()}
                    companyDetails={companyDetails}
                    confirmCallback={() => renewalSubscription()}
                />
            }
        </>
    );
}

function Reactivate({ setShowReactivationModal }) {
    return (
        <SettingsInfoBox background="#FFF5F5" border="#F3B5B5">
            <SettingsBodyText color="#D64545">
                Your subscription will not renew after the end date. Reactivate it to continue your subscription.
            </SettingsBodyText>
            <SettingsDangerActionButton onClick={() => setShowReactivationModal(true)}>
                Reactivate
            </SettingsDangerActionButton>
        </SettingsInfoBox>
    );
}

function Expired({ setShowRenewalModal }) {
    return (
        <SettingsInfoBox background="#FFF5F5" border="#F3B5B5">
            <SettingsBodyText color="#D64545">
                Your subscription has expired. Make a payment immediately to prevent cancellation.
            </SettingsBodyText>
            <SettingsDangerActionButton onClick={() => setShowRenewalModal(true)}>
                Renew
            </SettingsDangerActionButton>
        </SettingsInfoBox>
    );
}

export default withRouter(Subscription);
